import dayjs from "dayjs";
import { getMasterOptionName } from "./StepperDyanmicForm/formDataHelper";
import * as Yup from "yup";
import { JOURNEY_EMAIL_REGEX } from "../../utils/emailValidation";

export type Option = { label: string; value: string | number };

export const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

export const getAutocompleteOptionLabel = (value: any): string => {
  if (value && typeof value === "object") {
    return String(value.label ?? value.value ?? "").trim();
  }

  return String(value ?? "").trim();
};

export const getAutocompleteOptionValue = (value: any): string => {
  if (value && typeof value === "object") {
    return String(value.value ?? value.label ?? "").trim();
  }

  return String(value ?? "").trim();
};

const hasAutocompleteSelection = (value: any) =>
  Boolean(getAutocompleteOptionLabel(value) || getAutocompleteOptionValue(value));

export const resolveOptionLabel = (value: any, options: Option[] = []): string => {
  const rawLabel = getAutocompleteOptionLabel(value);
  const rawValue = getAutocompleteOptionValue(value);

  const match = options.find((opt) => {
    const optValue = String(opt.value ?? "").trim().toLowerCase();
    const optLabel = String(opt.label ?? "").trim().toLowerCase();
    return (
      (rawValue && optValue === rawValue.toLowerCase()) ||
      (rawLabel && optLabel === rawLabel.toLowerCase())
    );
  });

  return match?.label ?? rawLabel;
};

export const getCoApplicantFieldNames = (nextCoApplicantNumber: number) => {
  const coAppPrefix = `basicCoApplicant${nextCoApplicantNumber}`;
  return {
    coAppPrefix,
    relationshipField: `${coAppPrefix}RelationWithStudent`,
    relationValueField: `${coAppPrefix}OtherRelationWithStudent`,
    salutationField: `${coAppPrefix}salutation`,
    firstNameField: `${coAppPrefix}FirstName`,
    lastNameField: `${coAppPrefix}LastName`,
    genderField: `${coAppPrefix}Gender`,
    dobField: `${coAppPrefix}dob`,
    panField: `${coAppPrefix}PanNumber`,
    phoneField: `${coAppPrefix}PhoneNumber`,
    emailField: `${coAppPrefix}Email`,
    nationalityField: `${coAppPrefix}Nationality`,
    maritalStatusField: `${coAppPrefix}MaritalStatus`,
    countryOfBirthField: `${coAppPrefix}CountryOfBirth`,
    cityOfBirthField: `${coAppPrefix}CityOfBirth`,
    payPartyField: `${coAppPrefix}PayParty`,
  };
};

export const getCoApplicantEmpFieldNames = (nextCoApplicantNumber: number) => {
  const coAppPrefix = `employmentCoApplicant${nextCoApplicantNumber}`;
  return {
    coAppPrefix,
    isEmployedField: `${coAppPrefix}isEmployed`,  
    employmentTypeField: `${coAppPrefix}employmentType`,
    monthlyObligation: `${coAppPrefix}monthlyObligation`,
    companyName: `${coAppPrefix}companyName`,
    designation: `${coAppPrefix}designation`, 
    monthlyIncome: `${coAppPrefix}monthlyIncome`,   
    employmentYear: `${coAppPrefix}employmentYear`, 
    employmentMonth: `${coAppPrefix}employmentMonth`,     
    companyAddress1: `${coAppPrefix}companyAddress1`,   
    companyAddress2: `${coAppPrefix}companyAddress2`,                                                                                               
    companyAddress3: `${coAppPrefix}companyAddress3`,   
    landmark: `${coAppPrefix}landmark`,        
    pincode: `${coAppPrefix}pincode`,  
    city: `${coAppPrefix}city`, 
    state: `${coAppPrefix}state`,
    companyCountry: `${coAppPrefix}companyCountry`,                                                                  
  };
};

export const getCoApplicantAddressFieldNames = (nextCoApplicantNumber: number) => {
    const prefix = `addressCoApplicant${nextCoApplicantNumber}`;
    return {
      permanentOwnershipStatus: `${prefix}permanentOwnershipStatus`,
      permanentYearsInCurrentAddress: `${prefix}permanentYearsInCurrentAddress`,
      permanentYearsInCurrentCity: `${prefix}permanentYearsInCurrentCity`,
      permanentAddressLine1: `${prefix}permanentAddressLine1`,
      permanentAddressLine2: `${prefix}permanentAddressLine2`,
      permanentAddressLine3: `${prefix}permanentAddressLine3`,
      permanentPincode: `${prefix}permanentPincode`,
      permanentCity: `${prefix}permanentCity`,
      permanentState: `${prefix}permanentState`,
      permanentCountry: `${prefix}permanentCountry`,
      currentsameAsPermanent: `${prefix}currentsameAsPermanent`,
      currentOwnershipStatus: `${prefix}currentOwnershipStatus`,
      currentYearsInCurrentAddress: `${prefix}currentYearsInCurrentAddress`,
      currentYearsInCurrentCity: `${prefix}currentYearsInCurrentCity`,
      currentAddressLine1: `${prefix}currentAddressLine1`,
      currentAddressLine2: `${prefix}currentAddressLine2`,
      currentAddressLine3: `${prefix}currentAddressLine3`,
      currentPincode: `${prefix}currentPincode`,
      currentCity: `${prefix}currentCity`,
      currentState: `${prefix}currentState`,
      currentCountry: `${prefix}currentCountry`,
    };
};

export const getValidationSchema = (
  fields: any,
  requirePayParty: boolean = false
) =>
  Yup.object().shape({
    [fields.relationshipField]: Yup.string()
      .trim()
      .required("Relationship with Student is required"),
    [fields.relationValueField]: Yup.string().when(fields.relationshipField, {
      is: "OTHERS",
      then: (schema) =>
        schema
          .trim()
          .required("Specify Relationship is required")
          .matches(/^[A-Za-z\s]*$/, "Only characters allowed, no special characters.")
          .max(50, "Maximum 50 characters allowed."),
      otherwise: (schema) => schema.optional(),
    }),
    [fields.salutationField]: Yup.string()
      .trim()
      .required("How should we address you? is required"),
    [fields.firstNameField]: Yup.string()
      .trim()
      .required("Co-Applicant First Name is required")
      .max(26, "Maximum 26 characters allowed."),
    [fields.lastNameField]: Yup.string()
      .trim()
      .required("Co-Applicant Last Name is required")
      .max(26, "Maximum 26 characters allowed."),
    [fields.genderField]: Yup.string().trim().required("Gender is required"),
    [fields.dobField]: Yup.date()
      .nullable()
      .typeError("Date of Birth is required")
      .required("Date of Birth is required"),
    [fields.panField]: Yup.string()
      .trim()
      .required("PAN is required")
      .test("pan-format", "PAN must be in format AAAAA9999A", (value) =>
        value ? PAN_REGEX.test(value.toUpperCase()) : false
      ),
    [fields.phoneField]: Yup.string()
      .trim()
      .required("Co-Applicant's Mobile Number is required")
      .matches(/^[0-9]{10}$/, "Co-Applicant's Mobile Number must be 10 digits"),
    [fields.emailField]: Yup.string()
      .trim()
      .required("Co-Applicant's Email ID is required")
      .email("Invalid email id")
      .matches(JOURNEY_EMAIL_REGEX, "Invalid email id")
      .max(40, "Co-Applicant's Email ID must not exceed 40 characters"),
    [fields.nationalityField]: Yup.mixed().test(
      "nationality-required",
      "Nationality is required",
      (value) => hasAutocompleteSelection(value)
    ),
    [fields.maritalStatusField]: Yup.mixed().test(
      "marital-status-required",
      "Marital Status is required",
      (value) => hasAutocompleteSelection(value)
    ),
    [fields.countryOfBirthField]: Yup.mixed().test(
      "country-of-birth-required",
      "Country of Birth is required",
      (value) => hasAutocompleteSelection(value)
    ),
    [fields.cityOfBirthField]: Yup.mixed().test(
      "city-of-birth-required",
      "City of Birth is required",
      (value) => hasAutocompleteSelection(value)
    ),
    [fields.isEmployedField]: Yup.string()
      .trim()
      .required("Are you currently employed? is required"),
    [fields.payPartyField]: requirePayParty
      ? Yup.string().trim().required("Co-Applicant's Pay Party is required")
      : Yup.string().trim().optional(),
    [fields.permanentOwnershipStatus]: Yup.string()
      .trim()
      .required("Permanent Ownership Status is required"),
    [fields.permanentYearsInCurrentAddress]: Yup.number()
      .typeError("Please enter a valid number")
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .required("Permanent years at current address is required")
      .min(0, "Years must be at least 0")
      .max(150, "Years cannot exceed 150"),
    [fields.permanentYearsInCurrentCity]: Yup.number()
      .typeError("Please enter a valid number")
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .required("Permanent years in current city is required")
      .min(0, "Years must be at least 0")
      .max(150, "Years cannot exceed 150"),
    [fields.permanentAddressLine1]: Yup.string()
      .trim()
      .required("Permanent Address Line 1 is required")
      .max(50, "Maximum 50 characters allowed."),
    [fields.permanentAddressLine2]: Yup.string()
      .trim()
      .max(50, "Maximum 50 characters allowed.")
      .optional(),
    [fields.permanentAddressLine3]: Yup.string()
      .trim()
      .max(50, "Maximum 50 characters allowed.")
      .optional(),
    [fields.permanentPincode]: Yup.string()
      .trim()
      .required("Permanent Pincode is required")
      .matches(/^[0-9]{6}$/, "Permanent Pincode must be 6 digits"),
    [fields.permanentCity]: Yup.string()
      .trim()
      .required("Permanent City is required"),
    [fields.permanentState]: Yup.string()
      .trim()
      .required("Permanent State is required"),
    [fields.permanentCountry]: Yup.string()
      .trim()
      .required("Permanent Country is required"),
    [fields.currentsameAsPermanent]: Yup.boolean().optional(),
    [fields.currentOwnershipStatus]: Yup.string()
      .trim()
      .required("Current Ownership Status is required"),
    [fields.currentYearsInCurrentAddress]: Yup.number()
      .typeError("Please enter a valid number")
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .required("Current years at current address is required")
      .min(0, "Years must be at least 0")
      .max(150, "Years cannot exceed 150"),
    [fields.currentYearsInCurrentCity]: Yup.number()
      .typeError("Please enter a valid number")
      .nullable()
      .transform((value, originalValue) => (originalValue === "" ? null : value))
      .required("Current years in current city is required")
      .min(0, "Years must be at least 0")
      .max(150, "Years cannot exceed 150"),
    [fields.currentAddressLine1]: Yup.string()
      .trim()
      .required("Current Address Line 1 is required")
      .max(50, "Maximum 50 characters allowed."),
    [fields.currentAddressLine2]: Yup.string()
      .trim()
      .max(50, "Maximum 50 characters allowed.")
      .optional(),
    [fields.currentAddressLine3]: Yup.string()
      .trim()
      .max(50, "Maximum 50 characters allowed.")
      .optional(),
    [fields.currentPincode]: Yup.string()
      .trim()
      .required("Current Pincode is required")
      .matches(/^[0-9]{6}$/, "Current Pincode must be 6 digits"),
    [fields.currentCity]: Yup.string()
      .trim()
      .required("Current City is required"),
    [fields.currentState]: Yup.string()
      .trim()
      .required("Current State is required"),
    [fields.currentCountry]: Yup.string()
      .trim()
      .required("Current Country is required"),


    // Employment Details Validation 
    [fields.isEmployedField]: Yup.string()
      .trim()
      .required("Currently Employed is required"),

    [fields.employmentTypeField]: Yup.string().when(fields.isEmployedField, {
      is: "Yes",
      then: (schema) => schema.trim().required("Employement Type is required"),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.monthlyObligation]: Yup.string().when(fields.isEmployedField, {
      is: "Yes",
      then: (schema) => schema.trim().required("Monthly Financial Commitments isrequired."),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.monthlyIncome]: Yup.string().when(fields.isEmployedField, {
      is: "Yes",
      then: (schema) => schema.trim().required("Monthly Income isrequired."),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.companyName]: Yup.string().when(fields.employmentTypeField, {
      is: (value: string) => value && (value !== "Others" && value !== "Farmer"),
      then: (schema) => schema.trim().required("Company Name is required"),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.designation]: Yup.string().when(fields.employmentTypeField, {
      is: (value: string) => value && (value !== "Others" && value !== "Farmer"),
      then: (schema) => schema.trim().required("Designation is required"),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.employmentYear]: Yup.string().when(fields.employmentTypeField, {
      is: (value: string) => value && (value !== "Others" && value !== "Farmer"),
      then: (schema) =>
        schema
          .trim()
          .min(0, "Year cannot be negative.")
          .max(60, "Year cannot be greater than 60.")
          .required("Year is required"),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.employmentMonth]: Yup.string().when(fields.employmentTypeField, {
      is: (value: string) => value && (value !== "Others" && value !== "Farmer"),
      then: (schema) =>
        schema
          .trim()
          .min(0, "Month cannot be negative.")
          .max(12, "Month cannot be greater than 12.")
          .required("Month is required"),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.landmark]: Yup.string().when(fields.employmentTypeField, {
      is: (value: string) => value && (value !== "Others" && value !== "Farmer"),
      then: (schema) =>
        schema
          .trim()
          .max(50, "Maximum 50 characters allowed.")
          .required("Landmark is required"),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.companyAddress1]: Yup.string().when(fields.employmentTypeField, {
      is: (value: string) => value && (value !== "Others" && value !== "Farmer"),
      then: (schema) =>
        schema
          .trim()
          .max(50, "Maximum 50 characters allowed.")
          .required("Company Address Line 1 is required"),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.companyAddress2]: Yup.string().when(fields.employmentTypeField, {
      is: (value: string) => value && (value !== "Others" && value !== "Farmer"),
      then: (schema) =>
        schema
          .trim()
          .max(50, "Maximum 50 characters allowed.")
          .required("Company Address Line 2 is required"),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.companyAddress3]: Yup.string().when(fields.employmentTypeField, {
      is: (value: string) => value && (value !== "Others" && value !== "Farmer"),
      then: (schema) =>
        schema
          .trim()
          .max(50, "Maximum 50 characters allowed.")
          .required("Company Address Line 3 is required"),
      otherwise: (schema) => schema.trim().optional(),
    }),

    [fields.pincode]: Yup.string().when(fields.employmentTypeField, {
      is: (value: string) => value && (value !== "Others" && value !== "Farmer"),
      then: (schema) =>
        schema
          .trim()
          .required("Pincode is required")
          .matches(/^[0-9]{6}$/, "Pincode must be 6 digits"),
      otherwise: (schema) => schema.trim().optional(),
    }),
  });

export const normalizeValues = (
  values: any,
  fields: ReturnType<typeof getCoApplicantFieldNames>,
  maritalStatusOptions: Option[] = []
) => ({
  dob: values[fields.dobField]
    ? dayjs(values[fields.dobField] as Date).format("YYYY-MM-DD")
    : "",
  email: String(values[fields.emailField] || "").trim().toLowerCase(),
  phone: String(values[fields.phoneField] || "").trim(),
  pan: String(values[fields.panField] || "")
    .trim()
    .toUpperCase(),
  nationality: getAutocompleteOptionLabel(values[fields.nationalityField]),
  nationalityId: getAutocompleteOptionValue(values[fields.nationalityField]),
  maritalStatus: resolveOptionLabel(values[fields.maritalStatusField], maritalStatusOptions),
  maritalStatusId: getAutocompleteOptionValue(values[fields.maritalStatusField]),
  countryOfBirth: getAutocompleteOptionLabel(values[fields.countryOfBirthField]),
  countryOfBirthId: getAutocompleteOptionValue(values[fields.countryOfBirthField]),
  cityOfBirth: getAutocompleteOptionLabel(values[fields.cityOfBirthField]),
  cityOfBirthId: getAutocompleteOptionValue(values[fields.cityOfBirthField]),
});

export const createFieldIdResolver = (
  coApplicantFields: any[],
  nextCoApplicantNumber: number,
  storeUserFieldsData?: any[] | null
) => {
  const idByName = new Map<string, number>();
  if (Array.isArray(storeUserFieldsData)) {
    for (const row of storeUserFieldsData) {
      const key = row?.name ?? row?.fieldKey;
      const rawId = row?.id;
      if (key != null && rawId != null) {
        const n = Number(rawId);
        if (Number.isFinite(n) && n > 0) {
          idByName.set(String(key), n);
        }
      }
    }
  }

  const getAliasFieldKeys = (fieldKey: string) => {
    if (fieldKey.endsWith("OtherRelationWithStudent")) {
      return [fieldKey.replace(/OtherRelationWithStudent$/, "RelationValue")];
    }

    if (fieldKey.endsWith("RelationValue")) {
      return [fieldKey.replace(/RelationValue$/, "OtherRelationWithStudent")];
    }

    return [];
  };

  return (fieldKey: string) => {
    const fromStore = idByName.get(fieldKey);
    if (fromStore != null) return fromStore;

    for (const aliasKey of getAliasFieldKeys(fieldKey)) {
      const aliasFromStore = idByName.get(aliasKey);
      if (aliasFromStore != null) return aliasFromStore;
    }

    const exact = coApplicantFields.find(
      (f: any) => f?.name === fieldKey || f?.fieldKey === fieldKey
    );
    if (exact?.id != null) return exact.id;

    for (const aliasKey of getAliasFieldKeys(fieldKey)) {
      const aliasMatch = coApplicantFields.find(
        (f: any) => f?.name === aliasKey || f?.fieldKey === aliasKey
      );
      if (aliasMatch?.id != null) return aliasMatch.id;
    }

    const normalized = fieldKey.replace(
      `CoApplicant${nextCoApplicantNumber}`,
      "CoApplicant1"
    );
    const fallback = coApplicantFields.find(
      (f: any) => f?.name === normalized || f?.fieldKey === normalized
    );
    if (fallback?.id != null) return fallback.id;

    for (const aliasKey of getAliasFieldKeys(normalized)) {
      const aliasFallback = coApplicantFields.find(
        (f: any) => f?.name === aliasKey || f?.fieldKey === aliasKey
      );
      if (aliasFallback?.id != null) return aliasFallback.id;
    }

    return fallback?.id ?? null;
  };
};

export const buildAddressSaveFormData = ({
    fields,
    findFieldId,
    values,
  }: { 
    fields: any;
    findFieldId : (key: string) => number | null;
    values: any
  }) => {
    return [
      {
        formFieldId: findFieldId(fields.permanentOwnershipStatus),
        fieldKey: fields.permanentOwnershipStatus,
        fieldValue: values[fields.permanentOwnershipStatus],
      },
      {
        formFieldId: findFieldId(fields.permanentYearsInCurrentAddress),
        fieldKey: fields.permanentYearsInCurrentAddress,
        fieldValue: values[fields.permanentYearsInCurrentAddress],
      }, 
      {
        formFieldId: findFieldId(fields.permanentYearsInCurrentCity),
        fieldKey: fields.permanentYearsInCurrentCity,
        fieldValue: values[fields.permanentYearsInCurrentCity],
      }, 
      {
        formFieldId: findFieldId(fields.permanentAddressLine1),
        fieldKey: fields.permanentAddressLine1,
        fieldValue: values[fields.permanentAddressLine1],
      }, 
      {
        formFieldId: findFieldId(fields.permanentAddressLine2),
        fieldKey: fields.permanentAddressLine2,
        fieldValue: values[fields.permanentAddressLine2],
      }, 
      {
        formFieldId: findFieldId(fields.permanentAddressLine3),
        fieldKey: fields.permanentAddressLine3,
        fieldValue: values[fields.permanentAddressLine3],
      }, 
      {
        formFieldId: findFieldId(fields.permanentPincode),
        fieldKey: fields.permanentPincode,
        fieldValue: values[fields.permanentPincode],
      }, 
      {
        formFieldId: findFieldId(fields.permanentCity),
        fieldKey: fields.permanentCity,
        fieldValue: values[fields.permanentCity],
      }, 
      {
        formFieldId: findFieldId(fields.permanentState),
        fieldKey: fields.permanentState,
        fieldValue: values[fields.permanentState],
      }, 
      {
        formFieldId: findFieldId(fields.permanentCountry),
        fieldKey: fields.permanentCountry,
        fieldValue: values[fields.permanentCountry],
      }, 
      {
        formFieldId: findFieldId(fields.currentsameAsPermanent),
        fieldKey: fields.currentsameAsPermanent,
        fieldValue: values[fields.currentsameAsPermanent],
      }, 
      // Current Address
      {
        formFieldId: findFieldId(fields.currentOwnershipStatus),
        fieldKey: fields.currentOwnershipStatus,
        fieldValue: values[fields.currentOwnershipStatus],
      },
      {
        formFieldId: findFieldId(fields.currentYearsInCurrentAddress),
        fieldKey: fields.currentYearsInCurrentAddress,
        fieldValue: values[fields.currentYearsInCurrentAddress],
      }, 
      {
        formFieldId: findFieldId(fields.currentYearsInCurrentCity),
        fieldKey: fields.currentYearsInCurrentCity,
        fieldValue: values[fields.currentYearsInCurrentCity],
      }, 
      {
        formFieldId: findFieldId(fields.currentAddressLine1),
        fieldKey: fields.currentAddressLine1,
        fieldValue: values[fields.currentAddressLine1],
      }, 
      {
        formFieldId: findFieldId(fields.currentAddressLine2),
        fieldKey: fields.currentAddressLine2,
        fieldValue: values[fields.currentAddressLine2],
      }, 
      {
        formFieldId: findFieldId(fields.currentAddressLine3),
        fieldKey: fields.currentAddressLine3,
        fieldValue: values[fields.currentAddressLine3],
      }, 
      {
        formFieldId: findFieldId(fields.currentPincode),
        fieldKey: fields.currentPincode,
        fieldValue: values[fields.currentPincode],
      }, 
      {
        formFieldId: findFieldId(fields.currentCity),
        fieldKey: fields.currentCity,
        fieldValue: values[fields.currentCity],
      }, 
      {
        formFieldId: findFieldId(fields.currentState),
        fieldKey: fields.currentState,
        fieldValue: values[fields.currentState],
      }, 
      {
        formFieldId: findFieldId(fields.currentCountry),
        fieldKey: fields.currentCountry,
        fieldValue: values[fields.currentCountry],
      }, 
    ]
}

export const buildEmpSaveFormData = ({
    fields,
    findFieldId,
    values,
    designations,
  }: {
    fields: any;
    findFieldId : (key: string) => number | null;
    values: any;
    designations?: Array<{ value: any; label: any }>;
  }) => {
    return [
      {
      formFieldId: findFieldId(`${fields.coAppPrefix}isEmployed`),
      fieldKey: `${fields.coAppPrefix}isEmployed`,
      fieldValue: values[`${fields.coAppPrefix}isEmployed`],
    },
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}employmentType`),
      fieldKey: `${fields.coAppPrefix}employmentType`,
      fieldValue: values[`${fields.coAppPrefix}employmentType`],
    },
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}monthlyObligation`),
      fieldKey: `${fields.coAppPrefix}monthlyObligation`,
      fieldValue: values[`${fields.coAppPrefix}monthlyObligation`] || "",
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}companyName`),
      fieldKey: `${fields.coAppPrefix}companyName`,
      fieldValue: values[`${fields.coAppPrefix}companyName`] || "",
    },
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}designation`),
      fieldKey: `${fields.coAppPrefix}designation`,
      fieldValue: getMasterOptionName(values[`${fields.coAppPrefix}designation`], designations),
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}monthlyIncome`),
      fieldKey: `${fields.coAppPrefix}monthlyIncome`,
      fieldValue: values[`${fields.coAppPrefix}monthlyIncome`] || "",
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}employmentYear`),
      fieldKey: `${fields.coAppPrefix}employmentYear`,
      fieldValue: values[`${fields.coAppPrefix}employmentYear`] || "",
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}employmentMonth`),
      fieldKey: `${fields.coAppPrefix}employmentMonth`,
      fieldValue: values[`${fields.coAppPrefix}employmentMonth`] || "",
    },
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}companyAddress1`),
      fieldKey: `${fields.coAppPrefix}companyAddress1`,
      fieldValue: values[`${fields.coAppPrefix}companyAddress1`] || "",
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}companyAddress2`),
      fieldKey: `${fields.coAppPrefix}companyAddress2`,
      fieldValue: values[`${fields.coAppPrefix}companyAddress2`] || "",
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}companyAddress3`),
      fieldKey: `${fields.coAppPrefix}companyAddress3`,
      fieldValue: values[`${fields.coAppPrefix}companyAddress3`] || "",
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}landmark`),
      fieldKey: `${fields.coAppPrefix}landmark`,
      fieldValue: values[`${fields.coAppPrefix}landmark`] || "",
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}pincode`),
      fieldKey: `${fields.coAppPrefix}pincode`,
      fieldValue: values[`${fields.coAppPrefix}pincode`] || "",
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}city`),
      fieldKey: `${fields.coAppPrefix}city`,
      fieldValue: values[`${fields.coAppPrefix}city`] || "",
    }, 
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}state`),
      fieldKey: `${fields.coAppPrefix}state`,
      fieldValue: values[`${fields.coAppPrefix}state`] || "",
    },
    {
      formFieldId: findFieldId(`${fields.coAppPrefix}companyCountry`),
      fieldKey: `${fields.coAppPrefix}companyCountry`,
      fieldValue: values[`${fields.coAppPrefix}companyCountry`] || "",
    }, 
  ]
}

export const buildSaveFormData = ({
  fields,
  findFieldId,
  values,
  normalized,
  isPanVerified = false,
  nextCoApplicantNumber
}: {
  fields: ReturnType<typeof getCoApplicantFieldNames>;
  findFieldId: (key: string) => number | null;
  values: any;
  normalized: ReturnType<typeof normalizeValues>;
  isPanVerified?: boolean;
  nextCoApplicantNumber: number
}) => {
  const arr = [
  {
    formFieldId: findFieldId(fields.relationshipField),
    fieldKey: fields.relationshipField,
    fieldValue: values[fields.relationshipField] || "",
  },
  {
    formFieldId: findFieldId(fields.relationValueField),
    fieldKey: fields.relationValueField,
    fieldValue:
      values[fields.relationshipField] === "OTHERS"
        ? String(values[fields.relationValueField] || "").trim()
        : "",
  },
  {
    formFieldId: findFieldId(fields.salutationField),
    fieldKey: fields.salutationField,
    fieldValue: values[fields.salutationField] || "",
  },
  {
    formFieldId: findFieldId(fields.firstNameField),
    fieldKey: fields.firstNameField,
    fieldValue: String(values[fields.firstNameField] || "").trim(),
  },
  {
    formFieldId: findFieldId(fields.lastNameField),
    fieldKey: fields.lastNameField,
    fieldValue: String(values[fields.lastNameField] || "").trim(),
  },
  {
    formFieldId: findFieldId(fields.genderField),
    fieldKey: fields.genderField,
    fieldValue: values[fields.genderField] || "",
  },
  {
    formFieldId: findFieldId(fields.dobField),
    fieldKey: fields.dobField,
    fieldValue: normalized.dob,
  },
  {
    formFieldId: findFieldId(fields.panField),
    fieldKey: fields.panField,
    fieldValue: normalized.pan,
  },
  {
    formFieldId: findFieldId(fields.phoneField),
    fieldKey: fields.phoneField,
    fieldValue: normalized.phone,
  },
  {
    formFieldId: findFieldId(fields.emailField),
    fieldKey: fields.emailField,
    fieldValue: normalized.email,
  },
  {
    formFieldId: findFieldId(fields.nationalityField),
    fieldKey: fields.nationalityField,
    fieldValue: normalized.nationality,
  },
  {
    formFieldId: findFieldId(fields.maritalStatusField),
    fieldKey: fields.maritalStatusField,
    fieldValue: normalized.maritalStatus,
  },
  {
    formFieldId: findFieldId(fields.countryOfBirthField),
    fieldKey: fields.countryOfBirthField,
    fieldValue: normalized.countryOfBirth,
  },
  {
    formFieldId: findFieldId(fields.cityOfBirthField),
    fieldKey: fields.cityOfBirthField,
    fieldValue: normalized.cityOfBirth,
  },
  {
    formFieldId: findFieldId(`${fields.coAppPrefix}IsPanVerified`),
    fieldKey: `${fields.coAppPrefix}IsPanVerified`,
    fieldValue: isPanVerified,
  },
  {
    formFieldId: findFieldId(`${fields.coAppPrefix}IsMobileVerified`),
    fieldKey: `${fields.coAppPrefix}IsMobileVerified`,
    fieldValue: false,
  },
  {
    formFieldId: findFieldId(`${fields.coAppPrefix}IsEmailVerified`),
    fieldKey: `${fields.coAppPrefix}IsEmailVerified`,
    fieldValue: false,
  },
  ];
  arr.push({
    formFieldId: findFieldId(fields.payPartyField),
    fieldKey: fields.payPartyField,
    fieldValue:
      nextCoApplicantNumber === 1
        ? "Yes"
        : String(values[fields.payPartyField] || "").trim(),
  });
  return arr;
}

export const buildStoreUserFieldsPayload = ({
  nextCoApplicantNumber,
  trackingId,
  relationshipOptions,
  nationalityOptions = [],
  maritalStatusOptions = [],
  countryOptions = [],
}: {
  nextCoApplicantNumber: number;
  trackingId: string;
  relationshipOptions: Option[];
  nationalityOptions?: Option[];
  maritalStatusOptions?: Option[];
  countryOptions?: Option[];
}) => {
  const relationOptionsForStore = relationshipOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));
  const nationalityOptionsForStore = nationalityOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));
  const maritalStatusOptionsForStore = maritalStatusOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));
  const countryOptionsForStore = countryOptions.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));
  const salutationOptionsForStore = ["CA", "Dr.", "Er.", "Mr.", "Mrs.", "Ms.", "M/S"];
  const genderOptionsForStore = ["Male", "Female", "Other", "Prefer Not To Say"];
  const payPartyOptionsForStore = ["Yes", "No"];
  const ownershipOptionsForStore = ["Rented", "Owned"];
  const employedOptionsForStore = ["Yes", "No"];
  const employmentTypeOptionsForStore = ["Farmer", "Salaried", "SEP", "SENP", "Others"];

  return {
    vertical_step_id: 9,
    trackingId,
    fields: [
      { label: "Relationship with Student", name: `basicCoApplicant${nextCoApplicantNumber}RelationWithStudent`, type: "autocomplete", required: true, options: { options: relationOptionsForStore } },
      { label: "Specify Relationship", name: `basicCoApplicant${nextCoApplicantNumber}OtherRelationWithStudent`, required: false, type: "text", options: null },
      { label: "How should we address you? (Mr./Ms./etc.)", name: `basicCoApplicant${nextCoApplicantNumber}salutation`, type: "dropdown", required: true, options: { options: salutationOptionsForStore } },
      { label: "Co-Applicant First Name", name: `basicCoApplicant${nextCoApplicantNumber}FirstName`, type: "text", required: true },
      { label: "Co-Applicant Last Name", name: `basicCoApplicant${nextCoApplicantNumber}LastName`, type: "text", required: true },
      { label: "Gender", name: `basicCoApplicant${nextCoApplicantNumber}Gender`, type: "dropdown", options: { options: genderOptionsForStore }, required: true },
      { label: "Date of Birth", name: `basicCoApplicant${nextCoApplicantNumber}dob`, type: "date", required: true },
      { label: "PAN", name: `basicCoApplicant${nextCoApplicantNumber}PanNumber`, type: "text", required: true },
      { label: "Co-Applicant's Mobile Number", name: `basicCoApplicant${nextCoApplicantNumber}PhoneNumber`, type: "phone", required: true },
      { label: "Co-Applicant's Email ID", name: `basicCoApplicant${nextCoApplicantNumber}Email`, type: "email", required: true },
      { label: "PAN Verified", name: `basicCoApplicant${nextCoApplicantNumber}IsPanVerified`, type: "boolean", required: false },
      { label: "Mobile Verified", name: `basicCoApplicant${nextCoApplicantNumber}IsMobileVerified`, type: "boolean", required: false },
      { label: "Email Verified", name: `basicCoApplicant${nextCoApplicantNumber}IsEmailVerified`, type: "boolean", required: false },
      { label: "Aadhaar Verified", name: `basicCoApplicant${nextCoApplicantNumber}AadhaarVerified`, type: "boolean", required: false },
      { label: "Nationality", name: `basicCoApplicant${nextCoApplicantNumber}Nationality`, type: "autocomplete", required: true, options: { options: nationalityOptionsForStore } },
      { label: "Marital Status", name: `basicCoApplicant${nextCoApplicantNumber}MaritalStatus`, type: "dropdown", required: true, options: { options: maritalStatusOptionsForStore } },
      { label: "Country of Birth", name: `basicCoApplicant${nextCoApplicantNumber}CountryOfBirth`, type: "autocomplete", required: true, options: { options: countryOptionsForStore } },
      { label: "City of Birth", name: `basicCoApplicant${nextCoApplicantNumber}CityOfBirth`, type: "autocomplete", required: true, options: null },
      { label: "Do you want to consider income of the co-applicant for loan evaluation?", name: `basicCoApplicant${nextCoApplicantNumber}PayParty`, type: "radio", required: true, options: { options: payPartyOptionsForStore } },
      { label: "Owner Ship Status", name: `addressCoApplicant${nextCoApplicantNumber}permanentOwnershipStatus`, type: "dropdown", required: true, options: { options: ownershipOptionsForStore } },
      { label: "Year in current Address", name: `addressCoApplicant${nextCoApplicantNumber}permanentYearsInCurrentAddress`, type: "number", required: true },
      { label: "Year in current City", name: `addressCoApplicant${nextCoApplicantNumber}permanentYearsInCurrentCity`, type: "number", required: true },
      { label: "Address Line 1", name: `addressCoApplicant${nextCoApplicantNumber}permanentAddressLine1`, type: "text", required: true },
      { label: "Address Line 2", name: `addressCoApplicant${nextCoApplicantNumber}permanentAddressLine2`, type: "text", required: false },
      { label: "Address Line 3", name: `addressCoApplicant${nextCoApplicantNumber}permanentAddressLine3`, type: "text", required: false },
      { label: "Pincode", name: `addressCoApplicant${nextCoApplicantNumber}permanentPincode`, type: "text", required: true },
      { label: "City", name: `addressCoApplicant${nextCoApplicantNumber}permanentCity`, type: "autocomplete", required: true },
      { label: "State", name: `addressCoApplicant${nextCoApplicantNumber}permanentState`, type: "autocomplete", required: true },
      { label: "Country", name: `addressCoApplicant${nextCoApplicantNumber}permanentCountry`, type: "autocomplete", required: true },
      { label: "Keep Current Address same as Permanent Address", name: `addressCoApplicant${nextCoApplicantNumber}currentsameAsPermanent`, type: "checkbox", required: false },
      { label: "Owner Ship Status", name: `addressCoApplicant${nextCoApplicantNumber}currentOwnershipStatus`, type: "dropdown", required: true, options: { options: ownershipOptionsForStore } },
      { label: "Year in current Address", name: `addressCoApplicant${nextCoApplicantNumber}currentYearsInCurrentAddress`, type: "text", required: true },
      { label: "Year in current City", name: `addressCoApplicant${nextCoApplicantNumber}currentYearsInCurrentCity`, type: "text", required: true },
      { label: "Address Line 1", name: `addressCoApplicant${nextCoApplicantNumber}currentAddressLine1`, type: "text", required: true },
      { label: "Address Line 2", name: `addressCoApplicant${nextCoApplicantNumber}currentAddressLine2`, type: "text", required: false },
      { label: "Address Line 3", name: `addressCoApplicant${nextCoApplicantNumber}currentAddressLine3`, type: "text", required: false },
      { label: "Pincode", name: `addressCoApplicant${nextCoApplicantNumber}currentPincode`, type: "text", required: true },
      { label: "City", name: `addressCoApplicant${nextCoApplicantNumber}currentCity`, type: "autocomplete", required: true },
      { label: "State", name: `addressCoApplicant${nextCoApplicantNumber}currentState`, type: "autocomplete", required: true },
      { label: "Country", name: `addressCoApplicant${nextCoApplicantNumber}currentCountry`, type: "autocomplete", required: true },
      { label: "Are you employed?", name: `employmentCoApplicant${nextCoApplicantNumber}isEmployed`, type: "radio", required: true, options: { options: employedOptionsForStore } },
      { label: "Employment Type", name: `employmentCoApplicant${nextCoApplicantNumber}employmentType`, type: "dropdown", required: true, options: { options: employmentTypeOptionsForStore } },
      { label: "Monthly Obligation", name: `employmentCoApplicant${nextCoApplicantNumber}monthlyObligation`, type: "number", required: true },
      { label: "Company Name", name: `employmentCoApplicant${nextCoApplicantNumber}companyName`, type: "text", required: true },
      { label: "Designation", name: `employmentCoApplicant${nextCoApplicantNumber}designation`, type: "autocomplete", required: true },
      { label: "Monthly Income", name: `employmentCoApplicant${nextCoApplicantNumber}monthlyIncome`, type: "number", required: true },
      { label: "Year", name: `employmentCoApplicant${nextCoApplicantNumber}employmentYear`, type: "number", required: true },
      { label: "Month", name: `employmentCoApplicant${nextCoApplicantNumber}employmentMonth`, type: "number", required: true },
      { label: "Address Line 1", name: `employmentCoApplicant${nextCoApplicantNumber}companyAddress1`, type: "text", required: false },
      { label: "Address Line 2", name: `employmentCoApplicant${nextCoApplicantNumber}companyAddress2`, type: "text", required: false },
      { label: "Address Line 3", name: `employmentCoApplicant${nextCoApplicantNumber}companyAddress3`, type: "text", required: false },
      { label: "Landmark", name: `employmentCoApplicant${nextCoApplicantNumber}landmark`, type: "text", required: false },
      { label: "Pincode", name: `employmentCoApplicant${nextCoApplicantNumber}pincode`, type: "number", required: false },
      { label: "City", name: `employmentCoApplicant${nextCoApplicantNumber}city`, type: "text", required: false },
      { label: "State", name: `employmentCoApplicant${nextCoApplicantNumber}state`, type: "text", required: false },
      { label: "Country", name: `employmentCoApplicant${nextCoApplicantNumber}companyCountry`, type: "autocomplete", required: false },
    ],
  };
};
